<template>
  <v-card class="ma-2">
    <v-card-title class="pb-0" style="flex-direction: column; align-items: stretch">
      <v-layout row align-center style="border-bottom: 1px solid #ccc" class="pb-1 mb-2">
        <span class="subheading">{{ $t('Pilot.QuickControl.EasyWaypoint.Title') }}</span>
        <v-spacer />
        <v-switch
          hide-details
          class="sm-switch"
          v-model="active"
          @change="onToggle"
          :loading="toggling"
          style="flex: 0 0 auto"
        />
      </v-layout>
    </v-card-title>
    <v-card-text :class="{ disable: !active }">
      <!-- Mode de navigation (même section que MissionPlan) -->
      <div class="flex items-center mb-2 gap-4">
        <v-select
          :items="navModes"
          item-text="text"
          item-value="value"
          hide-details
          :label="$t('Simulation.NavigationMode')"
          v-model="navMode"
        />
        <div class="ml-auto" v-if="navMode === 'trackfollowing'">
          <number-field-factor
            hide-details
            class="mr-3"
            v-model.number="lookahead"
            :label="$t('Pilot.MissionPlan.Lookahead')"
          />
        </div>
      </div>
      <div class="flex items-center mb-3">
        {{ $t('Pilot.MissionPlan.CurrentCorrection') }}
        <v-switch
          hide-details
          class="pl-2 pb-2 sm-switch"
          v-model="enableCorrected"
          :label="$t('Pilot.MissionPlan.EnableCorrected')"
          style="flex: 0 0 auto"
        />
      </div>

      <!-- Coordonnées -->
      <v-layout row wrap>
        <v-flex xs6 class="pr-2">
          <lat-lng-field
            :value="lat"
            :is-lat="true"
            :format="coordFormatFilter"
            :label="$t('Pilot.QuickControl.EasyWaypoint.Lat')"
            hide-details
            @input="lat = $event"
          />
        </v-flex>
        <v-flex xs6 class="pl-2">
          <lat-lng-field
            :value="lon"
            :is-lat="false"
            :format="coordFormatFilter"
            :label="$t('Pilot.QuickControl.EasyWaypoint.Lon')"
            hide-details
            @input="lon = $event"
          />
        </v-flex>
      </v-layout>

      <!-- Profil nav optionnel -->
      <v-layout row align-center class="mt-3">
        <v-switch
          hide-details
          class="sm-switch mr-2"
          v-model="withNavProfile"
          :label="$t('Pilot.QuickControl.EasyWaypoint.WithNavProfile')"
          style="flex: 0 0 auto"
        />
      </v-layout>
      <div v-if="withNavProfile" class="mt-2">
        <v-divider class="mb-2" />
        <quick-control-nav-profile :value="localNavProfile" @input="localNavProfile = $event" />
      </div>

      <!-- Actions -->
      <v-layout row justify-end class="mt-3">
        <v-btn small :disabled="!isModified" @click="cancel">
          {{ $t('Pilot.QuickControl.EasyWaypoint.Restore') }}
          <v-icon right small>mdi-replay</v-icon>
        </v-btn>
        <v-btn
          small
          color="primary"
          :loading="saving"
          :disabled="!isModified || !canSave"
          @click="validate"
          class="ml-2"
        >
          {{ $t('Pilot.QuickControl.EasyWaypoint.Validate') }}
          <v-icon right small>mdi-content-save</v-icon>
        </v-btn>
      </v-layout>
    </v-card-text>
  </v-card>
</template>

<script>
import deepEqual from 'deep-equal'
import LatLngField from '@/components/shared/field/LatLngField.vue'
import QuickControlNavProfile from './QuickControlNavProfile.vue'
import { getDefaultQuickControlEasyWaypoint } from '@/models/iris/quickControlConfig/IQuickControlConfig'

const NAV_PROFILE_KEYS = ['zs', 'zt', 'zb', 'pu', 'pd', 'bu', 'bd', 'sr', 'alt']

export default {
  name: 'QuickControlEasyWaypoint',
  components: { LatLngField, QuickControlNavProfile },
  props: {
    misId: { type: Number, required: true },
    quickcontrolConfig: { type: Object, default: null },
    // IGliderRuntime — `runtime.targetLat/Lon` pour l'init coord si pas de
    // lat_target/lon_target déjà configurés, `runtime.navTemplate` pour le
    // profil nav par défaut.
    gliderRuntime: { type: Object, default: null },
  },
  data() {
    return {
      active: false,
      navMode: 'waypoint',
      lookahead: 1,
      enableCorrected: false,
      lat: null,
      lon: null,
      withNavProfile: false,
      localNavProfile: null,
      saving: false,
      toggling: false,
    }
  },
  watch: {
    quickcontrolConfig: {
      immediate: true,
      handler(val) {
        this.initFromConfig(val)
      },
    },
    navMode() {
      this.pushModified()
    },
    lookahead() {
      this.pushModified()
    },
    enableCorrected() {
      this.pushModified()
    },
    lat() {
      this.pushModified()
    },
    lon() {
      this.pushModified()
    },
    withNavProfile() {
      this.pushModified()
    },
    localNavProfile: {
      deep: true,
      handler() {
        this.pushModified()
      },
    },
  },
  computed: {
    coordFormatFilter() {
      return this.$store.myGetters.coordFormatFilter
    },
    navModes() {
      return [
        { value: 'waypoint', text: this.$t('Pilot.MissionPlan.Waypoint') },
        { value: 'heading', text: this.$t('Pilot.MissionPlan.Heading') },
        { value: 'trackfollowing', text: this.$t('Pilot.MissionPlan.Trackfollowing') },
      ]
    },
    canSave() {
      return this.lat != null && this.lon != null && !isNaN(this.lat) && !isNaN(this.lon)
    },
    originalConfig() {
      return this.$store.myGetters.originalQuickControlConfigs[this.misId] ?? null
    },
    // Bouton actif uniquement si le formulaire diverge de la config IRIS
    // sauvegardée. On dérive le snapshot de référence depuis la config
    // ORIGINALE via la même extraction (fallback runtime inclus) pour que
    // l'écran ne soit pas considéré « modifié » au simple chargement.
    isModified() {
      const current = {
        navMode: this.navMode,
        lookahead: this.lookahead,
        enableCorrected: this.enableCorrected,
        lat: this.lat,
        lon: this.lon,
        withNavProfile: this.withNavProfile,
        navProfile: this.localNavProfile,
      }
      return !deepEqual(current, this.extractFormValues(this.originalConfig))
    },
  },
  methods: {
    // Extrait les valeurs de formulaire d'une config (applique les fallbacks
    // runtime sur les coordonnées et le profil nav). Partagé par initFromConfig
    // (affichage) et isModified (snapshot de référence) pour rester cohérent.
    extractFormValues(config) {
      const settings = config?.mod_settings
      const ew = settings?.QuickControl_easywaypoint
      let lat = null
      let lon = null
      // Priorité 1 : lat_target/lon_target déjà configurés (≠ 0/0)
      const hasConfiguredCoords =
        ew?.lat_target != null && ew?.lon_target != null && !(ew.lat_target === 0 && ew.lon_target === 0)
      if (hasConfiguredCoords) {
        lat = ew.lat_target
        lon = ew.lon_target
      } else {
        // Priorité 2 : cible courante du glider
        const runtime = this.gliderRuntime
        if (runtime?.targetLat != null && runtime?.targetLon != null) {
          lat = runtime.targetLat
          lon = runtime.targetLon
        }
      }
      return {
        navMode: settings?.QuickControl?.current_mode ?? 'waypoint',
        lookahead: settings?.QuickControl?.lookahead ?? 1,
        enableCorrected: settings?.QuickControl_current_correction?.enable ?? false,
        lat,
        lon,
        withNavProfile: ew?.navigation_profile ?? false,
        navProfile: this.buildNavProfile(ew),
      }
    },
    initFromConfig(config) {
      this.active = config?.mod_settings?.EnabledMOD?.['module-QuickControl'] === true
      const v = this.extractFormValues(config)
      this.navMode = v.navMode
      this.lookahead = v.lookahead
      this.enableCorrected = v.enableCorrected
      this.lat = v.lat
      this.lon = v.lon
      this.withNavProfile = v.withNavProfile
      this.localNavProfile = v.navProfile
    },
    buildNavProfile(ew) {
      // Si la config a déjà toutes les valeurs nav profile → on les reprend
      const hasConfigured = ew != null && NAV_PROFILE_KEYS.every(k => ew[k] != null)
      if (hasConfigured) {
        return NAV_PROFILE_KEYS.reduce((acc, k) => ({ ...acc, [k]: ew[k] }), {})
      }
      // Sinon fallback navTemplate du runtime, puis defaults
      const tpl = this.gliderRuntime?.navTemplate
      const defaults = getDefaultQuickControlEasyWaypoint()
      if (!tpl) return NAV_PROFILE_KEYS.reduce((acc, k) => ({ ...acc, [k]: defaults[k] }), {})
      // navTemplate.rate (SEANAV.data_rate) ↔ profile.sr
      return {
        zs: tpl.zs ?? defaults.zs,
        zt: tpl.zt ?? defaults.zt,
        zb: tpl.zb ?? defaults.zb,
        pu: tpl.pu ?? defaults.pu,
        pd: tpl.pd ?? defaults.pd,
        bu: tpl.bu ?? defaults.bu,
        bd: tpl.bd ?? defaults.bd,
        sr: tpl.rate ?? defaults.sr,
        alt: tpl.alt ?? defaults.alt,
      }
    },
    buildEasyWaypointBlock() {
      const defaults = getDefaultQuickControlEasyWaypoint()
      const profile = this.localNavProfile ?? {}
      return {
        lat_target: this.lat ?? defaults.lat_target,
        lon_target: this.lon ?? defaults.lon_target,
        navigation_profile: this.withNavProfile,
        zs: profile.zs ?? defaults.zs,
        zt: profile.zt ?? defaults.zt,
        zb: profile.zb ?? defaults.zb,
        pu: profile.pu ?? defaults.pu,
        pd: profile.pd ?? defaults.pd,
        bu: profile.bu ?? defaults.bu,
        bd: profile.bd ?? defaults.bd,
        sr: profile.sr ?? defaults.sr,
        alt: profile.alt ?? defaults.alt,
      }
    },
    buildConfigPayload(baseConfig) {
      const configPayload = JSON.parse(JSON.stringify(baseConfig || { mod_settings: {} }))
      if (!configPayload.mod_settings.QuickControl) configPayload.mod_settings.QuickControl = {}
      configPayload.mod_settings.QuickControl.current_mode = this.navMode
      configPayload.mod_settings.QuickControl.lookahead = this.lookahead
      if (!configPayload.mod_settings.QuickControl_current_correction)
        configPayload.mod_settings.QuickControl_current_correction = {}
      configPayload.mod_settings.QuickControl_current_correction.enable = this.enableCorrected
      configPayload.mod_settings.QuickControl_easywaypoint = this.buildEasyWaypointBlock()
      return configPayload
    },
    async onToggle(val) {
      this.toggling = true
      try {
        const configPayload = JSON.parse(JSON.stringify(this.quickcontrolConfig || { mod_settings: {} }))
        if (!configPayload.mod_settings.EnabledMOD) configPayload.mod_settings.EnabledMOD = {}
        configPayload.mod_settings.EnabledMOD['module-QuickControl'] = val
        await this.$store.myActions.MISSION_IRIS_QUICKCONTROL_CONFIG_SAVE_REQUEST({
          itemId: this.misId,
          config: configPayload,
        })
        this.$emit('saved')
      } catch (e) {
        this.active = !val
        console.error('EasyWaypoint toggle error', e)
      } finally {
        this.toggling = false
      }
    },
    pushModified() {
      if (!this.quickcontrolConfig) return
      const configPayload = this.buildConfigPayload(this.quickcontrolConfig)
      this.$store.myActions.MISSION_UPDATE_MODIFIED_QUICKCONTROL_CONFIG({ itemId: this.misId, config: configPayload })
    },
    cancel() {
      this.$store.myActions.MISSION_CANCEL_MODIFIED_QUICKCONTROL(this.misId)
      this.initFromConfig(this.quickcontrolConfig)
    },
    async validate() {
      if (!this.canSave) return
      this.saving = true
      try {
        const configPayload = this.buildConfigPayload(this.quickcontrolConfig)
        await this.$store.myActions.MISSION_IRIS_QUICKCONTROL_CONFIG_SAVE_REQUEST({
          itemId: this.misId,
          config: configPayload,
        })
        this.$emit('saved')
      } catch (e) {
        console.error('EasyWaypoint save error', e)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.disable {
  overflow: hidden !important;
  position: relative;
}
.disable::before {
  position: absolute;
  content: '';
  left: 0;
  right: 0;
  top: 0;
  height: 8000%;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 1;
}
</style>
